/**
 * Recurring Work Orders Page.
 *
 * Manage recurring WO templates and manually trigger WO generation.
 */

import { useState } from "react";
import { RefreshCw, Plus, Trash2, Play } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface RecurringWO {
  id: string;
  name: string;
  work_type: string;
  title_template: string;
  priority: string;
  schedule_type: string;
  schedule_value: number;
  next_due_at: string | null;
  last_generated_at: string | null;
  is_active: boolean;
  asset_id: string | null;
}

function RecurringWOsPage() {
  const qc = useQueryClient();
  const { data: templates, isLoading } = useQuery<RecurringWO[]>({
    queryKey: ["recurring-wos"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/recurring-wos")).data as RecurringWO[],
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/recurring-wos", data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-wos"] });
      toast.success("Recurring template created");
    },
    onError: () => toast.error("Failed to create template"),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/recurring-wos/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-wos"] });
      toast.success("Template deleted");
    },
    onError: () => toast.error("Failed to delete template"),
  });
  const generateMutation = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/recurring-wos/${id}/generate`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-wos"] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      toast.success("Work order generated");
    },
    onError: () => toast.error("Failed to generate work order"),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [titleTemplate, setTitleTemplate] = useState("");
  const [workType, setWorkType] = useState("inspection");
  const [days, setDays] = useState("30");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createMutation.mutateAsync({
      name,
      title_template: titleTemplate,
      work_type: workType,
      schedule_value: parseInt(days),
    });
    setShowCreate(false);
    setName("");
    setTitleTemplate("");
    setDays("30");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Recurring Work Orders
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Schedule work orders to auto-generate on an interval.
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> New Template
            </>
          )}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Template Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Monthly Lot Inspection"
              />
              <Input
                label="WO Title Template"
                value={titleTemplate}
                onChange={(e) => setTitleTemplate(e.target.value)}
                required
                placeholder="Lot Inspection {date}"
              />
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Work Type
                </label>
                <select
                  value={workType}
                  onChange={(e) => setWorkType(e.target.value)}
                  className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
                >
                  {["inspection", "maintenance", "repair", "conditioning"].map(
                    (t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ),
                  )}
                </select>
              </div>
              <Input
                label="Every N Days"
                type="number"
                min="1"
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create Template
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : templates && templates.length > 0 ? (
        <div className="space-y-3 stagger-children">
          {templates.map((t) => (
            <Card key={t.id}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-[var(--text-primary)]">
                    {t.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                    {t.work_type} &middot; Every {t.schedule_value} days
                    &middot; "{t.title_template}"
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Badge variant={t.is_active ? "success" : "default"}>
                      {t.is_active ? "Active" : "Paused"}
                    </Badge>
                    {t.next_due_at && (
                      <span className="text-xs text-[var(--text-tertiary)]">
                        Next: {new Date(t.next_due_at).toLocaleDateString()}
                      </span>
                    )}
                    {t.last_generated_at && (
                      <span className="text-xs text-[var(--text-tertiary)]">
                        Last:{" "}
                        {new Date(t.last_generated_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => generateMutation.mutate(t.id)}
                    isLoading={generateMutation.isPending}
                  >
                    <Play className="h-3.5 w-3.5" /> Generate
                  </Button>
                  <button
                    onClick={() => deleteMutation.mutate(t.id)}
                    className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <RefreshCw />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No recurring templates
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create a template to auto-generate work orders on a schedule.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default RecurringWOsPage;
